import type { SpotKeyPermissionsSeen, SpotKeyReason, SpotKeyState } from "@auto-pools/core";

export type SpotKeyTone = "neutral" | "warn" | "good" | "bad";

export interface SpotKeyStateText {
  tone: SpotKeyTone;
  title: string;
  description: string;
}

/**
 * The twelve states of the key block (design `53`), verbatim. Four reasons reuse an existing
 * card's color with their own one-line text instead of a new layout, exactly as the contract's
 * `SpotKeyState`/`SpotKeyReason` doc describes; `holding` only changes the red/amber split for
 * `expired-or-revoked`.
 */
export function spotKeyStateText(
  state: SpotKeyState,
  reason: SpotKeyReason | null,
  holding: boolean,
): SpotKeyStateText {
  switch (state) {
    case "none":
      return {
        tone: "neutral",
        title: "No key yet",
        description: "Spot is not running. Pools are unaffected.",
      };
    case "pending":
      return {
        tone: "warn",
        title: "Pair made · waiting for the ID from Bybit",
        description:
          "The public key is ready to copy. Nothing else happens until an ID comes back.",
      };
    case "checking":
      return {
        tone: "warn",
        title: "Checking…",
        description:
          "Signing a request with the private key and asking Bybit what this key may do.",
      };
    case "working":
      return {
        tone: "good",
        title: "Working",
        description: "Spot trading only, on a subaccount, bound to the server IP.",
      };
    case "refused-permissions":
      if (reason === "not-uta")
        return {
          tone: "bad",
          title: "Refused · not a unified trading account",
          description: "The subaccount is a classic account. Spot needs a Unified Trading Account.",
        };
      if (reason === "different-subaccount")
        return {
          tone: "bad",
          title: "Refused · a different subaccount",
          description:
            "A spot run or an open order already belongs to the active key's subaccount; a new key must belong to the same one.",
        };
      return {
        tone: "bad",
        title: "Refused · too many permissions",
        description:
          "This key can move money — transfer, withdraw, Earn, fiat or card — or has permissions this server does not know. Make one with only SPOT → Trade ticked; a key that can move money out is not stored.",
      };
    case "key-mismatch":
      return {
        tone: "bad",
        title: "Key does not match",
        description:
          "Bybit rejects our signature. The ID probably belongs to another key, or the public key was pasted only in part. Copy it again, whole.",
      };
    case "not-bound":
      return {
        tone: "warn",
        title: "Not bound to the server IP",
        description:
          "It works, but it will stop after 90 days. Amber the whole time it stays unbound, not only at the end.",
      };
    case "read-only":
      return {
        tone: "warn",
        title: "Read only",
        description:
          "The key can read balances but not trade. The bot would see everything and do nothing.",
      };
    case "main-account":
      return {
        tone: "warn",
        title: "Main account key",
        description:
          "This key belongs to the main account. The bot claims every coin it can see — give it a subaccount of its own.",
      };
    case "expiring":
      return {
        tone: "warn",
        title: "Expiring",
        description:
          "The end of the 90 days an unbound key gets. Bind the next one to the server IP and this state never comes back.",
      };
    case "expired-or-revoked":
      if (reason === "unreadable")
        return {
          tone: "bad",
          title: "Stored key cannot be read — replace it",
          description: "The saved private key failed to open. Replacing it creates a new pair.",
        };
      return {
        tone: holding ? "bad" : "warn",
        title: "Expired or revoked",
        description: holding
          ? "Red only while coins are held: the bot cannot sell them if BTC turns down."
          : "Flat, it is amber.",
      };
    case "bybit-not-answering":
      if (reason === "clock-skew")
        return {
          tone: "warn",
          title: "Clock out of sync with Bybit",
          description: "Re-measured every minute; clears automatically once the clocks agree.",
        };
      return {
        tone: "warn",
        title: "Bybit is not answering",
        description: "The key itself may be fine.",
      };
  }
}

/**
 * The server keeps the public key as bare SPKI DER base64, but Bybit's Self-generated key form
 * refuses that ("The format of the public key you entered is incorrect") and accepts only PEM:
 * the header, the body in 64-character lines, the footer.
 */
export function toPem(spkiBase64: string): string {
  const lines = spkiBase64.match(/.{1,64}/g) ?? [];
  return ["-----BEGIN PUBLIC KEY-----", ...lines, "-----END PUBLIC KEY-----"].join("\n");
}

/** `Group:Permission` names as "Group · Permission, …". */
const permissionNames = (extra: string[]): string =>
  extra.map((name) => name.replace(":", " · ")).join(", ");

/**
 * The offending grants behind a permission refusal, so the operator knows which box to untick:
 * "Bybit lists extra permissions: Wallet · Withdraw, …". Null when it lists nothing.
 */
export function permissionsSeenText(seen: SpotKeyPermissionsSeen): string | null {
  const sentences: string[] = [];
  if (seen.extra.length > 0)
    sentences.push(`Bybit lists extra permissions: ${permissionNames(seen.extra)}.`);
  if (seen.missingSpotTrade) sentences.push("Spot · SpotTrade is not granted.");
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/** The trading-only extras an accepted key carries (e.g. the Unified Trading grant Bybit adds). */
export function toleratedPermissionsText(seen: SpotKeyPermissionsSeen): string | null {
  return seen.extra.length > 0
    ? `Bybit also granted: ${permissionNames(seen.extra)} — this server only places spot orders.`
    : null;
}
